import {CubeGridLoader} from "../../loading/CubeGridLoader";
import {ColorPicker, Segmented, Tooltip} from "antd";
import {Layers2, Plus, Trash2, Upload, X, ZoomIn, ZoomOut} from "lucide-react";
import React, {MouseEvent, useEffect, useRef, useState, WheelEvent} from "react";
import {useImageEnhancerEditorInfoStore} from "../../../store/imageEnhancerEditorInfoStore";
import html2canvas from "html2canvas";
import {basicDownloadImage} from "../../../utils";
import {createInferenceSession, doImgEnhancer, recreateInferenceSession} from "./imgEnhancer";
import {useAlertMessage} from "../../../hooks/AlertContext";
import {getCurModelIndex, modelExists, setModelIndex} from "../../../utils/model";
import {ModelTypeConstant} from "../../../constants";
import {ProgressLoader} from "../../loading/ProgressLoader";
import {modelIsLoaded} from "./imgEnhancer";
import {useNotificationMessage} from "../../../hooks/NotificationContext";

type IImageEnhancerEditorProps = {
    closeEditorTable?: () => void;
    modelKeys: any;
}

const maxWidthSize = 900;// 图片宽最大为900px
const maxHeightSize = 600;// 图片高最大为600px
const minWidthSize  = 300;// 图片宽最小为300px
const minHeightSize = 300; // 图片高最小为 300px

const minScale= 1;// 图片最小放缩倍率
const maxScale = 10;// 图片最大放缩倍率
const zoomIntensity = 0.1;// 图片缩放速度
export const ImageEnhancerEditor: React.FC<IImageEnhancerEditorProps> = ({
    closeEditorTable = () => {},
    modelKeys
}) => {
    const {showAlert} = useAlertMessage();
    const {showLoadingNotification} = useNotificationMessage();

    const {finalCanvasUrl, originalCanvasUrl, setOriginalCanvasUrl, setFinalCanvasUrl} = useImageEnhancerEditorInfoStore();

    const canvasContainerRef = React.createRef<HTMLDivElement>();

    const finalCanvasRef = React.createRef<HTMLImageElement>();
    const originalCanvasRef = React.createRef<HTMLImageElement>();

    const [generateProgress, setGenerateProgress] = useState({
        totalSteps: 100,
        currentStep: 0,
    });// 图像生成的进度条
    const [isLoading, setIsLoading] = useState(true);
    const [isConverted, setConverted] = useState(false);// 图像是否已经转换完成，即处理完成

    const [scale, setScale] = useState(1);// 当前图片的缩放率
    const [transformOrigin, setTransformOrigin] = useState('50% 50%');// 缩放基点
    const [translateX, setTranslateX] = useState(0);// 图片X轴平移
    const [translateY, setTranslateY] = useState(0);// 图片Y轴平移
    const dragState = useRef({ isDragging: false, startX: 0, startY: 0 }); // 拖动状态

    const [isShowOriginalImg, setIsShowOriginalImg] = useState(false);// 是否显示原始图像
    const [layers2Color, setLayers2Color] = useState('black');// layers2图标的颜色（开启原始图像就显示其他颜色，关闭则是默认黑色）

    const fileInputRef = React.createRef<HTMLInputElement>();// 文件上传ref

    const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });

    useEffect(() => {// 监听窗口大小变化
        const handleResize = () => {
            setWindowSize({ width: window.innerWidth, height: window.innerHeight });
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const getReasonableSize = (width: number, height: number) => {
        const aspectRatio = width / height; // 原始宽高比

        let newWidth = width;
        let newHeight = height;

        // 根据窗口大小调整图片宽度
        if (windowSize.width < maxWidthSize) {
            newWidth = windowSize.width * 0.5; // 图片宽度为窗口宽度的 50%
            newHeight = newWidth / aspectRatio; // 保持宽高比
        }

        // 如果调整后的尺寸超出最大限制，按最大限制调整
        if (newWidth > maxWidthSize || newHeight > maxHeightSize) {
            if (newWidth > maxWidthSize) {
                newWidth = maxWidthSize;
                newHeight = newWidth / aspectRatio;
            }
            if (newHeight > maxHeightSize) {
                newHeight = maxHeightSize;
                newWidth = newHeight * aspectRatio;
            }
        }

        // 如果调整后的尺寸小于最小限制，按最小限制调整
        if (newWidth < minWidthSize || newHeight < minHeightSize) {
            if (newWidth < minWidthSize) {
                newWidth = minWidthSize;
                newHeight = newWidth / aspectRatio;
            }
            if (newHeight < minHeightSize) {
                newHeight = minHeightSize;
                newWidth = newHeight * aspectRatio;
            }
        }

        return { newWidth, newHeight };
    }

    useEffect(() => {
        const container = canvasContainerRef.current;
        if (finalCanvasUrl && container && finalCanvasRef.current) {
            const img = new Image();
            img.src = finalCanvasUrl;
            img.onload = () => {
                const {newWidth, newHeight} = getReasonableSize(img.width, img.height);
                container.style.minWidth = `min(${newWidth}px, 100%)`;
                container.style.maxWidth = '100%';
                container.style.width = newWidth + 'px';
                container.style.minHeight = `min(80%, ${newHeight}px)`;
                container.style.maxHeight = '80%';
                container.style.height = newHeight + 'px';
            };
        }
    }, [finalCanvasUrl]);

    useEffect(() => {
        const container = canvasContainerRef.current;
        if (originalCanvasUrl && container && originalCanvasRef.current) {
            const img = new Image();
            img.src = originalCanvasUrl;
            img.onload = () => {
                const {newWidth, newHeight} = getReasonableSize(img.width, img.height);
                container.style.minWidth = `min(${newWidth}px, 100%)`;
                container.style.maxWidth = '100%';
                container.style.width = newWidth + 'px';
                container.style.minHeight = `min(80%, ${newHeight}px)`;
                container.style.maxHeight = '80%';
                container.style.height = newHeight + 'px';
            };
        }
    }, [originalCanvasUrl, windowSize]);

    useEffect(() => {
        setIsLoading(false);
    }, [finalCanvasRef.current]);

    const setIsShowOriginalImgState = () => {// 设置是否显示原始图像
        setLayers2Color(isShowOriginalImg ? 'black' : '#C56D00');
        setIsShowOriginalImg(!isShowOriginalImg);
    }

    const uploadFile = async (ev: any) => {// 重新上传文件
        if (!modelIsLoaded()) {
            showAlert({
                message: "Loading model and trying again later",
                type: "warning",
            })
            return;
        }
        if (isLoading) {// 正在加载中
            showAlert({
                message: "A new task is running!!",
                type: "warning"
            })
            return;
        }
        setIsLoading(false);//重置

        const file = ev.currentTarget.files?.[0];
        if (file) {
            setIsLoading(true);
            setGenerateProgress({
                totalSteps: 100,
                currentStep: 0
            });

            const imageUrl = URL.createObjectURL(file);
            setOriginalCanvasUrl(imageUrl);
            doImgEnhancer(imageUrl, setGenerateProgress, showAlert).then((res: any) => {
                if (!res) return;
                const { blobUrl } = res;
                // @ts-ignore
                setFinalCanvasUrl(blobUrl);
                setLayers2Color('black');
                setIsShowOriginalImg(false);// 强制显示最终图像

                setConverted(true);
                setIsLoading(false);
            }).catch((error) => {
                console.error("图像转换失败，" + error);
                showAlert({
                    message: "Failed to process enhance",
                    type: "error"
                })
                setIsLoading(false);
            });
        }
    }

    const handleUpload = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';// 重置文件输入值
            fileInputRef.current.click();
        }
    }

    const zoomInImage = () => {
        const newScale = scale + zoomIntensity;
        if (newScale < minScale || newScale > maxScale) return;
        setScale(newScale);
    }

    const zoomOutImage = () => {
        const newScale = scale - zoomIntensity;
        if (newScale < minScale || newScale > maxScale) return;
        setScale(newScale);

        // 重置平移当缩放到等于 1
        if (newScale === 1) {
            setTranslateX(0);
            setTranslateY(0);
            setTransformOrigin('50% 50%');
        }
    }

    const zoomImage = (e: WheelEvent) => {
        // 计算缩放比例
        const delta = e.deltaY;
        const newScale = delta > 0 ? scale - zoomIntensity : scale + zoomIntensity;

        if (newScale < minScale || newScale > maxScale) return;

        // 计算鼠标相对位置
        const rect = canvasContainerRef.current?.getBoundingClientRect();
        if (!rect) return;

        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        setTransformOrigin(`${mouseX}px ${mouseY}px`);
        setScale(newScale);

        if (newScale === 1) {
            // 重置平移当缩放到等于 1
            setTranslateX(0);
            setTranslateY(0);
            setTransformOrigin('50% 50%');// 重置缩放基点

            // 触发一次拖动事件
            const startX = mouseX, startY = mouseY;
            const endX = mouseX + 50, endY = mouseY + 50;
            if (canvasContainerRef.current) {
                // 触发拖拽开始
                const dragStartEvent = new DragEvent('dragstart', { bubbles: true });
                dragStartEvent.dataTransfer?.setData('text/plain', 'item-id');
                canvasContainerRef.current.dispatchEvent(dragStartEvent);

                // 触发拖拽移动
                const dragEvent = new DragEvent('drag', {
                    clientX: startX,
                    clientY: startY
                });
                canvasContainerRef.current.dispatchEvent(dragEvent);

                // 触发拖拽结束
                const dragEndEvent = new DragEvent('dragend', {
                    clientX: endX,
                    clientY: endY
                });
                canvasContainerRef.current.dispatchEvent(dragEndEvent);
            }
        }
    }

    // 鼠标按下
    const handleMouseDown = (event: MouseEvent) => {
        if (scale <= 1) return; // 仅当放大后才允许拖动

        event.preventDefault();
        dragState.current = {
            isDragging: true,
            startX: event.clientX - translateX,
            startY: event.clientY - translateY,
        };
    };

    // 鼠标移动
    const handleMouseMove = (event: MouseEvent) => {
        if (!dragState.current.isDragging) return;

        let newTranslateX = event.clientX - dragState.current.startX;
        let newTranslateY = event.clientY - dragState.current.startY;

        const img = isShowOriginalImg ? originalCanvasRef.current : finalCanvasRef.current;
        if (img) {
            const rect = img.getBoundingClientRect();
            const maxTranslateX = (rect.width * scale - rect.width) / 2;
            const maxTranslateY = (rect.height * scale - rect.height) / 2;

            newTranslateX = Math.min(Math.max(newTranslateX, -maxTranslateX), maxTranslateX);
            newTranslateY = Math.min(Math.max(newTranslateY, -maxTranslateY), maxTranslateY);
            setTranslateX(
                newTranslateX
            );
            setTranslateY(
                newTranslateY
            );
        }
    };

    // 鼠标松开
    const handleMouseUp = () => {
        dragState.current.isDragging = false;
    };

    // 鼠标离开容器时停止拖动
    const handleMouseLeave = () => {
        dragState.current.isDragging = false;
    };

    const downloadImage = async () => {
        if (!finalCanvasRef.current) return;
        const finalCanvas = finalCanvasRef.current;
        try {
            const tempImg = new Image();
            tempImg.crossOrigin = "anonymous";
            tempImg.src = finalCanvasUrl;
            if (finalCanvas.style.backgroundClip === 'content-box')// 带有透明背景属性的img
                tempImg.style.backgroundColor = '';// 设置空背景
            else {
                tempImg.style.backgroundColor = finalCanvas.style.backgroundColor;
                tempImg.style.backgroundImage = finalCanvas.style.backgroundImage;
                tempImg.style.backgroundSize = finalCanvas.style.backgroundSize;
            }
            tempImg.style.position = 'fixed';
            tempImg.style.top = '-99999999px';
            document.body.appendChild(tempImg);
            let canvas = await html2canvas(tempImg, {
                useCORS: true,
                logging: true,
                backgroundColor: 'transparent',
                scale: 3,
            });
            basicDownloadImage(canvas.toDataURL('image/png', 10), `IMG_${Date.now()}.png`);

            tempImg.remove();
        } catch (error) {
            console.error('Image Download Error:', error);
        }
    };

    const handleEnhance = async (modelKey: number) => {
        if (!modelIsLoaded()) {
            showAlert({
                message: "Loading model and trying again later",
                type: "warning",
            })
            return;
        }
        if (isLoading) {// 正在加载中
            showAlert({
                message: "A new task is running!!",
                type: "warning"
            })
            return;
        }

        setIsLoading(true);
        setGenerateProgress({
            totalSteps: 100,
            currentStep: 0
        });
        if (modelKey !== getCurModelIndex(ModelTypeConstant.SUPER_RESOLUTION)) {
            setModelIndex(ModelTypeConstant.SUPER_RESOLUTION, modelKey);
            await recreateInferenceSession(showAlert, showLoadingNotification);
        }

        doImgEnhancer(originalCanvasUrl,setGenerateProgress, showAlert).then((res: any) => {
            if (!res) return;
            const { blobUrl } = res;

            setFinalCanvasUrl(blobUrl);
            setLayers2Color('black');
            setIsShowOriginalImg(false);// 强制显示最终图像

            setConverted(true);
            setIsLoading(false);
        }).catch((error) => {
            console.error("图像转换失败，" + error);
            showAlert({
                message: "Failed to process enhance",
                type: "error"
            })
            setIsLoading(false);
        })
    }

    return (
        <div className="i-editor-container" onContextMenu={(e) => e.preventDefault()}>
            <div className="i-editor-contain">
                <div className="i-left-container">
                    <div className="i-editor">
                        <div
                            // @ts-ignore
                            ref={canvasContainerRef}
                            className="canvas-container cursor-grab active:cursor-grabbing"
                            onWheel={zoomImage}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseLeave}
                            onDragStart={(e) => e.preventDefault()}
                        >
                            {/*<CubeGridLoader*/}
                            {/*    dotBackgroundColor="#3498db"*/}
                            {/*    isOpen={isLoading}*/}
                            {/*/>*/}
                            <ProgressLoader
                                isOpen={isLoading}
                                totalSteps={generateProgress.totalSteps}
                                currentStep={generateProgress.currentStep}
                            />
                            {/*最终图像*/}
                            {!isShowOriginalImg &&
                                <img
                                    // @ts-ignore
                                    ref={finalCanvasRef}
                                    src={finalCanvasUrl}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        transform: `scale(${scale}) translate(${translateX}px, ${translateY}px)`,
                                        transition: dragState.current.isDragging ? 'none' : 'transform 0.1s ease-out',
                                        transformOrigin: transformOrigin
                                    }}
                                />
                            }
                            {/*原始图像*/}
                            {isShowOriginalImg &&
                                <img
                                    // @ts-ignore
                                    ref={originalCanvasRef}
                                    src={originalCanvasUrl}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        transform: `scale(${scale}) translate(${translateX}px, ${translateY}px)`,
                                        transition: dragState.current.isDragging ? 'none' : 'transform 0.1s ease-out',
                                        transformOrigin: transformOrigin,
                                    }}
                                />
                            }
                        </div>
                        <div className="i-bottom-opt-button">
                            <div></div>
                            <div className="flex gap-[5%]">
                                <Tooltip placement="bottom" title={isShowOriginalImg ? 'Processed img' : 'Original img'}>
                                    <button onClick={setIsShowOriginalImgState}>
                                        <Layers2 style={{width: '20px', height: '18px', color: layers2Color}}/>
                                    </button>
                                </Tooltip>
                                <Tooltip placement="bottom" title="Zoom in">
                                    <button onClick={zoomInImage}>
                                        <ZoomIn style={{width: '20px', height: '18px'}}/>
                                    </button>
                                </Tooltip>
                                <Tooltip placement="bottom" title="Zoom out">
                                    <button onClick={zoomOutImage}>
                                        <ZoomOut style={{width: '20px', height: '18px'}}/>
                                    </button>
                                </Tooltip>
                                <Tooltip placement="bottom" title="Reupload">
                                    <button onClick={handleUpload}>
                                        <Upload style={{width: '20px', height: '18px'}}/>
                                        <input
                                            // @ts-ignore
                                            ref={fileInputRef}
                                            type="file"
                                            className="hidden"
                                            onChange={uploadFile}
                                            accept="image/png, image/jpeg, image/webp"
                                        />
                                    </button>
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="i-right-container">
                    <div className="i-landing-tool-business-button">
                        <div className="flex flex-row">
                            <Tooltip placement="bottom" title="enhance image">
                                <div className="ordinary-opt-button i-opt-button" onClick={() => handleEnhance(modelKeys.NORMAL_MODEL)}>
                                    HD
                                </div>
                            </Tooltip>
                            <Tooltip placement="bottom" title="enhance image">
                                <div className="vip-opt-button i-opt-button" onClick={() => handleEnhance(modelKeys.HD_MODEL)}>
                                    Ultra HD
                                </div>
                            </Tooltip>
                        </div>
                        <div className="flex flex-row">
                            <Tooltip placement="bottom" title="enhance the anime image">
                                <div className="bg-[#007BFF] hover:bg-[#0056b3] i-opt-button" onClick={() => handleEnhance(modelKeys.ANIME_HD_MODEL)}>
                                    Anime HD
                                </div>
                            </Tooltip>
                        </div>
                        {isConverted &&
                            <>
                                <Tooltip placement="bottom" title="Up to original size">
                                    <div className="ordinary-opt-button i-opt-button" onClick={downloadImage}>
                                        Download Free
                                    </div>
                                </Tooltip>
                            </>
                        }
                    </div>

                    <div className="absolute top-0 right-[6px]" onClick={closeEditorTable}>
                        <button className="i-close-btn">
                            <X style={{width: '20px', height: '18px'}}/>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
